import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Typography, Input, Switch, Space, Spin, Alert, Empty, Button, theme } from 'antd';
import { message } from '../../../services/toastMessage';
import { ReloadOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import { getCountries, ICountryResponse } from '../../../services/LocaleService';
import {
  getAllowedCountries,
  addAllowedCountry,
  removeAllowedCountry,
  refreshAllowedCountry,
  refreshAllAllowedCountries,
  IAllowedCountry,
} from '../../../services/AdminService';
import { TRANSLATIONS } from '../../../constants/translations';
import { translate } from '../../../helpers/translationHelper';

const { Title, Text } = Typography;
const { Search } = Input;

// Every country is BLOCKED by default; turning a switch on allows that
// country to register (UserProfileController.CreateMyProfile) and publish
// spaces (HeavyDutyMachineryController), via the shared GeoBlockHelper. The
// first time a country is switched on, the backend fetches its IP ranges
// once from MaxMind (see IpRangeSyncService) -- switching it off and back
// on again does nothing further. The per-row refresh button and the
// "Actualizar todos" button above force a re-fetch on demand (e.g. MaxMind
// publishes a new release) without waiting on anything automatic --
// there's no scheduled job at all. Deliberately no "select all" bulk
// toggle here: firing that for every country at once is what made the
// backend unresponsive on every deploy before.
const GeoBlockingSection: React.FC = () => {
  const { token } = theme.useToken();
  const { t } = useTranslation();
  const [countries, setCountries] = useState<ICountryResponse[]>([]);
  const [allowedByCode, setAllowedByCode] = useState<Record<string, IAllowedCountry>>({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [search, setSearch] = useState('');
  const [togglingCodes, setTogglingCodes] = useState<Set<string>>(new Set());
  const [refreshingCodes, setRefreshingCodes] = useState<Set<string>>(new Set());
  const [refreshingAll, setRefreshingAll] = useState(false);

  const load = useCallback(() => {
    setLoading(true);
    setError(false);
    Promise.all([getCountries(), getAllowedCountries()])
      .then(([countryList, allowed]) => {
        setCountries(countryList);
        setAllowedByCode(Object.fromEntries(allowed.map((a) => [a.countryCode, a])));
      })
      .catch(() => setError(true))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const setInSet = (setter: React.Dispatch<React.SetStateAction<Set<string>>>, code: string, value: boolean) => {
    setter((prev) => {
      const next = new Set(prev);
      if (value) next.add(code);
      else next.delete(code);
      return next;
    });
  };

  const handleToggle = async (country: ICountryResponse, allow: boolean) => {
    setInSet(setTogglingCodes, country.alpha2Code, true);
    try {
      if (allow) {
        await addAllowedCountry(country.alpha2Code, country.name);
      } else {
        const allowed = allowedByCode[country.alpha2Code];
        if (allowed) await removeAllowedCountry(allowed.id);
      }
      await load();
    } catch {
      message.error(translate(t, TRANSLATIONS.ADMIN_ACTION_ERROR));
    } finally {
      setInSet(setTogglingCodes, country.alpha2Code, false);
    }
  };

  const handleRefresh = async (allowed: IAllowedCountry) => {
    setInSet(setRefreshingCodes, allowed.countryCode, true);
    try {
      await refreshAllowedCountry(allowed.id);
      await load();
    } catch {
      message.error(translate(t, TRANSLATIONS.ADMIN_ACTION_ERROR));
    } finally {
      setInSet(setRefreshingCodes, allowed.countryCode, false);
    }
  };

  const handleRefreshAll = async () => {
    setRefreshingAll(true);
    try {
      await refreshAllAllowedCountries();
      message.success(translate(t, TRANSLATIONS.ADMIN_ACTION_SUCCESS));
      await load();
    } catch {
      message.error(translate(t, TRANSLATIONS.ADMIN_ACTION_ERROR));
    } finally {
      setRefreshingAll(false);
    }
  };

  const filteredCountries = useMemo(() => {
    const sorted = [...countries].sort((a, b) => a.name.localeCompare(b.name));
    const query = search.trim().toLowerCase();
    if (!query) return sorted;
    return sorted.filter((c) => c.name.toLowerCase().includes(query) || c.alpha2Code.toLowerCase().includes(query));
  }, [countries, search]);

  const hasAnyAllowed = Object.keys(allowedByCode).length > 0;

  const formatLastSynced = (value: string | null) => {
    if (!value) return translate(t, TRANSLATIONS.ADMIN_GEO_BLOCKING_NEVER_SYNCED);
    return new Date(value).toLocaleString();
  };

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 16, flexWrap: 'wrap' }}>
        <div>
          <Title level={4} style={{ marginBottom: 8 }}>
            {translate(t, TRANSLATIONS.ADMIN_NAV_GEO_BLOCKING)}
          </Title>
          <Text type="secondary" style={{ display: 'block', marginBottom: 16 }}>
            {translate(t, TRANSLATIONS.ADMIN_GEO_BLOCKING_DESCRIPTION)}
          </Text>
        </div>
        <Button icon={<ReloadOutlined />} loading={refreshingAll} disabled={!hasAnyAllowed} onClick={handleRefreshAll}>
          {translate(t, TRANSLATIONS.ADMIN_GEO_BLOCKING_REFRESH_ALL_BUTTON)}
        </Button>
      </div>

      <Space style={{ marginBottom: 16 }} wrap>
        <Search
          allowClear
          placeholder={translate(t, TRANSLATIONS.ADMIN_GEO_BLOCKING_SEARCH_PLACEHOLDER)}
          style={{ maxWidth: 280, width: '100%' }}
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
      </Space>

      {loading ? (
        <div style={{ textAlign: 'center', padding: 32 }}>
          <Spin />
        </div>
      ) : error ? (
        <Alert type="error" showIcon message={translate(t, TRANSLATIONS.ADMIN_GEO_BLOCKING_LOAD_ERROR)} />
      ) : filteredCountries.length === 0 ? (
        <Empty description={translate(t, TRANSLATIONS.ADMIN_GEO_BLOCKING_NO_MATCHES)} />
      ) : (
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))',
            gap: '4px 24px',
            maxHeight: 560,
            overflowY: 'auto',
            border: `1px solid ${token.colorBorderSecondary}`,
            borderRadius: 8,
            padding: 16,
          }}
        >
          {filteredCountries.map((country) => {
            const allowedEntry = allowedByCode[country.alpha2Code];
            const allowed = !!allowedEntry;
            return (
              <div
                key={country.alpha2Code}
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  padding: '8px 0',
                  borderBottom: `1px solid ${token.colorBorderSecondary}`,
                  gap: 8,
                }}
              >
                <div>
                  <Text>{country.name}</Text>
                  {allowed && (
                    <>
                      <br />
                      <Text type="secondary" style={{ fontSize: 12 }}>
                        {translate(t, TRANSLATIONS.ADMIN_GEO_BLOCKING_LAST_SYNCED_LABEL)}: {formatLastSynced(allowedEntry.lastSyncedAt)}
                      </Text>
                    </>
                  )}
                </div>
                <Space>
                  {allowed && (
                    <Button
                      type="text"
                      size="small"
                      icon={<ReloadOutlined />}
                      loading={refreshingCodes.has(country.alpha2Code)}
                      onClick={() => handleRefresh(allowedEntry)}
                    />
                  )}
                  <Switch
                    checked={allowed}
                    loading={togglingCodes.has(country.alpha2Code)}
                    onChange={(checked) => handleToggle(country, checked)}
                  />
                </Space>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default GeoBlockingSection;
